<script setup lang="ts">
import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { markRaw, onBeforeUnmount, onMounted, ref, shallowRef, watch, type Ref } from 'vue';
import i18n from '@/i18n';
import { BASE_URL } from '@/libs/api-helper';
import Common from '@/libs/common';
import { localizeMapLabels } from '@/libs/map-language';
import { MAP_STYLE_URL, withTilesKey } from '@/libs/map-tiles';
import type { LatLong } from '@/stores/app';
import type { Group } from '@/@types/group';
import { getBoundsCenter } from './groupBounds';

const NEARBY_COUNT = 5;

// Default bounds covering Spain (south-west) to Germany (north-east).
// [west, south, east, north]
const DEFAULT_BOUNDS: [number, number, number, number] = [-9.5, 36.0, 15.0, 55.0];

const { t } = i18n.global;

const props = withDefaults(
  defineProps<{
    userPosition?: LatLong;
    groups?: Group[];
    /** Height the collapsed sheet hides at the bottom of the map, so a fit stays visible. */
    bottomInset?: number;
  }>(),
  { userPosition: undefined, groups: undefined, bottomInset: 0 },
);

const emit = defineEmits<{
  'select-group': [group: Group];
}>();

const mapdiv = ref<HTMLElement>() as Ref<HTMLElement>;
const mapInstance = shallowRef<maplibregl.Map>();
let activePopup: maplibregl.Popup | null = null;
let hasInitialFitted = false;

watch(
  () => props.userPosition,
  () => {
    updateUserPositionSource();
  },
);

watch(
  () => props.groups,
  () => {
    updateGroupsSource();
    fitToNearbyGroups();
  },
);

onMounted(async () => {
  const map = new maplibregl.Map({
    container: mapdiv.value,
    style: MAP_STYLE_URL,
    transformRequest: withTilesKey,
    attributionControl: { compact: true },
    touchPitch: false,
    bounds: DEFAULT_BOUNDS,
  });

  await new Promise<void>(resolve => {
    map.on('load', () => {
      resolve();
    });
  });
  localizeMapLabels(map, i18n.global.locale);

  map.addSource('groupCentroids', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'groupCentroidsBg',
    source: 'groupCentroids',
    type: 'circle',
    paint: {
      'circle-radius': 9,
      'circle-color': '#ffffff',
      'circle-stroke-width': 2,
      'circle-stroke-color': ['get', 'color'],
    },
  });
  map.addLayer({
    id: 'groupCentroids',
    source: 'groupCentroids',
    type: 'circle',
    paint: {
      'circle-radius': 5,
      'circle-color': ['get', 'color'],
    },
  });

  const canvas = map.getCanvasContainer();
  map.on('mouseenter', 'groupCentroidsBg', () => {
    canvas.style.cursor = 'pointer';
  });
  map.on('mouseleave', 'groupCentroidsBg', () => {
    canvas.style.cursor = '';
  });
  map.on('click', 'groupCentroidsBg', e => {
    if (!e.features || e.features.length === 0) return;
    const groupId = e.features[0].properties?.groupId;
    const group = props.groups?.find(g => g.group_id === groupId);
    if (group) showGroupPopup(group);
  });

  map.addSource('userPosition', {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer({
    id: 'userPosition',
    source: 'userPosition',
    type: 'circle',
    paint: {
      'circle-radius': 9,
      'circle-color': '#437af7',
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  });

  mapInstance.value = markRaw(map);
  updateGroupsSource();
  updateUserPositionSource();
});

onBeforeUnmount(() => {
  activePopup?.remove();
  activePopup = null;
  mapInstance.value?.remove();
});

function updateGroupsSource() {
  if (!mapInstance.value || !props.groups) return;

  const source = mapInstance.value.getSource('groupCentroids') as maplibregl.GeoJSONSource;
  if (!source) return;

  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (const group of props.groups) {
    if (!group.bounds) continue;
    const center = getBoundsCenter(group.bounds);
    if (!center) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.lon, center.lat],
      },
      properties: {
        groupId: group.group_id,
        color: group.color || '#00B871',
      },
    });
  }

  source.setData({
    type: 'FeatureCollection',
    features,
  });
}

function showGroupPopup(group: Group) {
  if (!mapInstance.value || !group.bounds) return;
  const center = getBoundsCenter(group.bounds);
  if (!center) return;

  if (activePopup) activePopup.remove();

  const container = document.createElement('div');
  container.className = 'select-group-map__popup';

  if (group.logo_url) {
    const logo = document.createElement('img');
    logo.className = 'select-group-map__popup-logo';
    logo.src = BASE_URL + group.logo_url;
    logo.alt = group.name;
    container.appendChild(logo);
  }

  const name = document.createElement('div');
  name.className = 'select-group-map__popup-name';
  name.textContent = group.name;

  const button = document.createElement('button');
  button.className = 'select-group-map__popup-button';
  button.textContent = t('selectGroup.selectConfirm');
  button.addEventListener('click', () => {
    emit('select-group', group);
    activePopup?.remove();
    activePopup = null;
  });

  container.appendChild(name);
  container.appendChild(button);

  activePopup = new maplibregl.Popup({ closeButton: true, closeOnClick: true, offset: 12 })
    .setLngLat([center.lon, center.lat])
    .setDOMContent(container)
    .addTo(mapInstance.value);

  activePopup.on('close', () => {
    activePopup = null;
  });
}

function updateUserPositionSource() {
  if (!mapInstance.value || !props.userPosition) return;

  const source = mapInstance.value.getSource('userPosition') as maplibregl.GeoJSONSource;
  if (!source) return;

  source.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [props.userPosition.lon, props.userPosition.lat],
        },
        properties: {},
      },
    ],
  });

  fitToNearbyGroups();
}

/**
 * If the user is geolocated, fit the map to the bounds of the user position
 * and the NEARBY_COUNT closest groups. Only runs once automatically per page load.
 */
function fitToNearbyGroups() {
  if (hasInitialFitted) return;
  if (!mapInstance.value || !props.userPosition || !props.groups?.length) return;
  hasInitialFitted = true;
  recenterOnNearbyGroups();
}

/**
 * Force re-fit the map to the user position and the NEARBY_COUNT closest groups.
 * Exposed to the parent so the GPS button can recenter on demand.
 */
function recenterOnNearbyGroups() {
  if (!mapInstance.value || !props.userPosition || !props.groups) return;

  const userCoord = {
    latitude: props.userPosition.lat,
    longitude: props.userPosition.lon,
  };

  const groupsWithCenter = props.groups
    .map(group => {
      if (!group.bounds) return null;
      const center = getBoundsCenter(group.bounds);
      if (!center) return null;
      const distance = Common.coordDistance(userCoord, {
        latitude: center.lat,
        longitude: center.lon,
      });
      return { center, distance };
    })
    .filter((g): g is { center: { lat: number; lon: number }; distance: number } => g !== null);

  const padding = { top: 40, left: 40, right: 40, bottom: 40 + props.bottomInset };

  if (groupsWithCenter.length === 0) {
    mapInstance.value.flyTo({
      center: { lat: props.userPosition.lat, lng: props.userPosition.lon },
      zoom: 13,
      padding,
    });
    return;
  }

  groupsWithCenter.sort((a, b) => a.distance - b.distance);
  const nearest = groupsWithCenter.slice(0, NEARBY_COUNT);

  const bounds = new maplibregl.LngLatBounds();
  bounds.extend([props.userPosition.lon, props.userPosition.lat]);
  for (const g of nearest) {
    bounds.extend([g.center.lon, g.center.lat]);
  }

  mapInstance.value.fitBounds(bounds, {
    padding,
    maxZoom: 13,
    duration: 500,
  });
}

defineExpose({ recenterOnNearbyGroups });
</script>

<template>
  <div id="select-group-mapdiv" ref="mapdiv" />
</template>

<style lang="scss" scoped>
#select-group-mapdiv {
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
}
</style>

<style lang="scss">
.maplibregl-popup-content {
  border-radius: 8px;
}
.maplibregl-popup-close-button {
  padding: 3px 7px;
}
.select-group-map__popup {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  padding: 4px 4px 0;
  min-width: 140px;
}

.select-group-map__popup-logo {
  max-width: 80px;
  max-height: 40px;
  object-fit: contain;
  align-self: center;
}

.select-group-map__popup-name {
  color: $color-neutral-highest;
  font: 600 14px $font-poppins;
  text-align: center;
}

.select-group-map__popup-button {
  background-color: $color-primary;
  color: $color-white;
  border: none;
  border-radius: 5px;
  padding: 8px 12px;
  font: 600 13px $font-poppins;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
}
</style>
